import { useState } from "react";
import { sendReceiptEmail } from "../services/receiptEmailService";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";

const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)",
  zIndex: 1000,
};
const MODAL_BASE = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  background: "#1a2535",
  border: "1px solid #2d3b4e",
  borderRadius: 12,
  padding: "24px",
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
  display: "flex",
  flexDirection: "column",
};

const PAGE_SIZE = 8;

function CloseBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        color: "#4b5e75",
        cursor: "pointer",
        fontSize: "1.1rem",
        flexShrink: 0,
      }}
      onMouseOver={(e) => (e.currentTarget.style.color = "#9ca3af")}
      onMouseOut={(e) => (e.currentTarget.style.color = "#4b5e75")}
    >
      ✕
    </button>
  );
}

function SummaryCard({ value, label, color, bg, border, icon }) {
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        padding: "10px 12px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          marginBottom: 3,
          color,
        }}
      >
        <i className={`fa-solid ${icon}`} style={{ fontSize: "0.75rem" }} />
        <span style={{ fontSize: "1.5rem", fontWeight: 800, lineHeight: 1 }}>
          {value}
        </span>
      </div>
      <div style={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 500 }}>
        {label}
      </div>
    </div>
  );
}

function SectionHeader({ icon, color, label }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 6,
        color,
        fontSize: "0.75rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      <i className={`fa-solid ${icon}`} style={{ fontSize: "0.68rem" }} />
      {label}
    </div>
  );
}

function PaginatedSection({ items, isError, isSinFactura, emptyLabel }) {
  const [page, setPage] = useState(1);
  if (!items || items.length === 0) return null;

  const pages = Math.ceil(items.length / PAGE_SIZE);
  const slice = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const safe = Math.min(page, pages);

  const bg =
    isError || isSinFactura ? "rgba(239,68,68,0.06)" : "rgba(74,222,128,0.06)";
  const bdr =
    isError || isSinFactura ? "rgba(239,68,68,0.15)" : "rgba(74,222,128,0.12)";
  const color = isError || isSinFactura ? "#f87171" : "#4ade80";
  const icon =
    isError || isSinFactura ? "fa-circle-exclamation" : "fa-circle-check";

  return (
    <div style={{ marginBottom: 14 }}>
      {slice.map((item, i) => {
        const recibo = typeof item === "string" ? item : item.recibo;
        const detail =
          typeof item === "string"
            ? isSinFactura
              ? "Sin facturas aplicadas"
              : ""
            : isError
              ? item.error
              : item.destinatario;
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "7px 10px",
              borderRadius: 6,
              background: bg,
              border: `1px solid ${bdr}`,
              marginBottom: 3,
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
              }}
            >
              <i
                className={`fa-solid ${icon}`}
                style={{ color, fontSize: "0.68rem", flexShrink: 0 }}
              />
              <span
                style={{
                  fontWeight: 600,
                  color: "#f1f5f9",
                  fontSize: "0.83rem",
                  whiteSpace: "nowrap",
                }}
              >
                {recibo}
              </span>
            </div>
            <span
              style={{
                color: isError || isSinFactura ? "#fca5a5" : "#9ca3af",
                fontSize: "0.77rem",
                textAlign: "right",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 200,
              }}
            >
              {detail}
            </span>
          </div>
        );
      })}

      {/* Paginación compacta */}
      {pages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid #2d3b4e",
          }}
        >
          <span style={{ color: "#6b7280", fontSize: "0.72rem" }}>
            {(safe - 1) * PAGE_SIZE + 1}–
            {Math.min(safe * PAGE_SIZE, items.length)} / {items.length}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <MiniPageBtn
              disabled={safe === 1}
              onClick={() => setPage((p) => p - 1)}
              label="‹"
            />
            {[...Array(pages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: safe === i + 1 ? "#f97316" : "#374151",
                  background:
                    safe === i + 1 ? "rgba(249,115,22,0.15)" : "transparent",
                  color: safe === i + 1 ? "#f97316" : "#6b7280",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {i + 1}
              </button>
            ))}
            <MiniPageBtn
              disabled={safe === pages}
              onClick={() => setPage((p) => p + 1)}
              label="›"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MiniPageBtn({ disabled, onClick, label }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 22,
        height: 22,
        borderRadius: 4,
        border: "1px solid #374151",
        background: "transparent",
        color: disabled ? "#374151" : "#9ca3af",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "0.85rem",
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {label}
    </button>
  );
}

export default function SendReceiptEmailModal({
  recibos = [],
  onClose,
  onSuccess,
}) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const multiple = recibos.length > 1;

  async function handleSend() {
    setSending(true);
    try {
      const ids = recibos.map((r) => r.id);
      const data = await sendReceiptEmail(ids);
      setResult(data);
      if (data.total_enviados > 0 && onSuccess) onSuccess();
    } catch (e) {
      setResult({
        enviados: [],
        errores: [{ recibo: "—", error: e.message }],
        sin_facturas: [],
        total_enviados: 0,
        total_errores: 1,
      });
    } finally {
      setSending(false);
    }
  }

  // ── Vista de resultado ───────────────────────────────────────────────
  if (result) {
    const sinFacturas = result.sin_facturas ?? [];
    const erroresReales = (result.errores ?? []).filter(
      (e) => !sinFacturas.includes(e.recibo),
    );
    const total =
      (result.enviados?.length ?? 0) +
      sinFacturas.length +
      erroresReales.length;

    return (
      <>
        <div onClick={onClose} style={OVERLAY} />
        <div
          style={{ ...MODAL_BASE, width: "min(560px,95vw)", maxHeight: "88vh" }}
        >
          {/* Header — siempre visible */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  background: "rgba(249,115,22,0.15)",
                  border: "1px solid rgba(249,115,22,0.3)",
                  borderRadius: 9,
                  padding: "8px 10px",
                  color: "#f97316",
                }}
              >
                <i
                  className="fa-solid fa-envelope"
                  style={{ fontSize: "1.1rem" }}
                />
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    color: "#f1f5f9",
                    fontSize: "1rem",
                    fontWeight: 700,
                  }}
                >
                  Resultado del envío
                </h3>
                <p
                  style={{
                    margin: "2px 0 0",
                    color: "#64748b",
                    fontSize: "0.75rem",
                  }}
                >
                  {total} recibo(s) procesado(s)
                </p>
              </div>
            </div>
            <CloseBtn onClick={onClose} />
          </div>

          {/* Cards resumen — siempre visibles */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            <SummaryCard
              value={result.total_enviados}
              label="Enviados"
              color="#4ade80"
              bg="rgba(74,222,128,0.08)"
              border="rgba(74,222,128,0.25)"
              icon="fa-circle-check"
            />
            <SummaryCard
              value={sinFacturas.length}
              label="Sin facturas"
              color="#fbbf24"
              bg="rgba(251,191,36,0.08)"
              border="rgba(251,191,36,0.25)"
              icon="fa-clock"
            />
            <SummaryCard
              value={erroresReales.length}
              label="Con error"
              color="#f87171"
              bg="rgba(239,68,68,0.08)"
              border="rgba(239,68,68,0.25)"
              icon="fa-circle-exclamation"
            />
          </div>

          <div
            style={{
              borderTop: "1px solid #2d3b4e",
              marginBottom: 14,
              flexShrink: 0,
            }}
          />

          {/* Listas scrollables */}
          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {result.total_enviados > 0 && (
              <>
                <SectionHeader
                  icon="fa-circle-check"
                  color="#4ade80"
                  label={`Enviados (${result.total_enviados})`}
                />
                <PaginatedSection
                  items={result.enviados}
                  isError={false}
                  isSinFactura={false}
                />
              </>
            )}

            {sinFacturas.length > 0 && (
              <>
                <SectionHeader
                  icon="fa-triangle-exclamation"
                  color="#fbbf24"
                  label={`Sin facturas aplicadas — no enviados (${sinFacturas.length})`}
                />
                <PaginatedSection
                  items={sinFacturas.map((n) => ({
                    recibo: n,
                    destinatario: "",
                  }))}
                  isError={false}
                  isSinFactura={true}
                />
              </>
            )}

            {erroresReales.length > 0 && (
              <>
                <SectionHeader
                  icon="fa-circle-exclamation"
                  color="#f87171"
                  label={`Errores de envío (${erroresReales.length})`}
                />
                <PaginatedSection
                  items={erroresReales}
                  isError={true}
                  isSinFactura={false}
                />
              </>
            )}
          </div>

          {/* Footer — siempre visible */}
          <div
            style={{
              borderTop: "1px solid #2d3b4e",
              marginTop: 16,
              paddingTop: 16,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "8px 20px",
                background: "#f97316",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                fontSize: "0.84rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Vista de confirmación ─────────────────────────────────────────────
  return (
    <>
      <div onClick={onClose} style={OVERLAY} />
      <div
        style={{ ...MODAL_BASE, width: "min(500px,93vw)", maxHeight: "85vh" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.3)",
                borderRadius: 9,
                padding: "8px 10px",
                color: "#f97316",
              }}
            >
              <i
                className="fa-solid fa-envelope"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "#f1f5f9",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {multiple
                  ? `Enviar ${recibos.length} correos de recibo`
                  : "Enviar correo del recibo"}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  color: "#64748b",
                  fontSize: "0.77rem",
                }}
              >
                Con PDF adjunto al email principal del propietario
              </p>
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>

        <div
          style={{
            borderTop: "1px solid #2d3b4e",
            marginBottom: 16,
            flexShrink: 0,
          }}
        />

        {/* Lista de recibos — scrollable */}
        <div
          style={{
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            maxHeight: 200,
            marginBottom: 14,
          }}
        >
          {recibos.map((r, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: 6,
                background: i % 2 === 0 ? "rgba(55,65,81,0.2)" : "transparent",
                marginBottom: 2,
                fontSize: "0.84rem",
              }}
            >
              <span style={{ fontWeight: 600, color: "#f1f5f9" }}>
                {r.numero_recibo}
              </span>
              <span style={{ color: "#9ca3af" }}>
                Filial {r.filial?.idFilial ?? "—"}
              </span>
              <span style={{ color: "#f97316", fontWeight: 600 }}>
                ₡{formatCurrency(r.monto)}
              </span>
            </div>
          ))}
        </div>

        {/* Aviso */}
        <div
          style={{
            background: "rgba(251,191,36,0.08)",
            border: "1px solid rgba(251,191,36,0.2)",
            borderLeft: "3px solid #fbbf24",
            borderRadius: 6,
            padding: "10px 14px",
            color: "#fde68a",
            fontSize: "0.82rem",
            marginBottom: 16,
            flexShrink: 0,
          }}
        >
          <i className="fa-solid fa-circle-info" style={{ marginRight: 7 }} />
          Solo se enviarán recibos con facturas aplicadas. Los pendientes serán
          reportados sin enviarse.
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid #2d3b4e",
              borderRadius: 7,
              color: "#64748b",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            style={{
              padding: "8px 20px",
              background: sending ? "rgba(249,115,22,0.5)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: sending ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {sending ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> Enviando…
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />
                {multiple
                  ? `Enviar ${recibos.length} correos`
                  : "Enviar correo"}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
